/** Вкладка «Поставки»: заявки с начислениями кросс-докинга и ошибок поставок + разбор по актам. */
import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useState } from "react";

import type { OzFilters, SupplyBy, SupplyRow } from "@/api/ozonAnalytics";
import { cn } from "@/lib/utils";
import { xdPerUnit, xdPerUnitLabel } from "./calc";
import { fmtDate, fmtDateTimeMsk, int, rub } from "./format";
import { useSupplies, useSupplyDetail } from "./queries";
import { CardButton, ErrorBox, Loading, Seg } from "./shared";
import type { CardRef } from "./state";
import { SupplyAccrualsSection, SupplyActSection, SupplyDiagSection } from "./SupplyDetail";
import { IssueChips } from "./SupplyIssues";
import { moneyClass, tdNum, thClass } from "./ui";

const MODES: { id: SupplyBy; label: string }[] = [
  { id: "invoice", label: "По месяцу начисления" },
  { id: "ship", label: "По дате отправки" },
];

const COLS = 9;

export function SuppliesTab({
  filters,
  by,
  onlyIssues,
  onPatch,
  onCard,
}: {
  filters: OzFilters;
  by: SupplyBy;
  onlyIssues: boolean;
  onPatch: (patch: Record<string, string | null>) => void;
  /** открыть карточку поставки (боковая панель) */
  onCard: (card: CardRef) => void;
}): React.ReactElement {
  const q = useSupplies(filters, by);
  const [open, setOpen] = useState<string | null>(null);
  const rows = (q.data?.rows ?? []).filter((r) => !onlyIssues || r.issue_count > 0);
  const t = q.data?.totals;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <Seg
          label="Как отбирать поставки"
          value={by}
          options={MODES}
          onChange={(v) => onPatch({ by: v === "invoice" ? null : v })}
        />
        <label className="flex items-center gap-1.5 text-sm">
          <input
            type="checkbox"
            checked={onlyIssues}
            onChange={(e) => onPatch({ iss: e.target.checked ? "1" : null })}
          />
          Только с проблемами
        </label>
        <span className="text-xs text-muted-foreground">
          {by === "invoice"
            ? "Заявки, по которым в периоде начислены кросс-докинг или ошибки поставок."
            : "Заявки со слотом в периоде и все их начисления — кросс-док и штрафы приходят позже отгрузки."}
        </span>
      </div>

      {q.isLoading && <Loading text="Собираю поставки…" />}
      {q.isError && <ErrorBox error={q.error} what="поставки" />}
      {q.data?.diag_stale && (
        <div
          role="alert"
          className="rounded-md border border-amber-500/50 bg-amber-50 dark:border-amber-700/60 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
        >
          Диагноз поставок (акты приёмки) устарел:{" "}
          {q.data.diag_as_of
            ? `собран ${fmtDateTimeMsk(q.data.diag_as_of)}, а витрина собрана позже`
            : "таблица диагноза пуста"}
          . Проблемы свежих заявок могут не отображаться.
        </div>
      )}
      {t && (
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
          <span>
            Заявок: <b className="tabular-nums">{int(t.count)}</b>, с проблемами:{" "}
            <b className="tabular-nums">{int(t.with_issues)}</b>
          </span>
          <span>
            Кросс-докинг: <b className={cn("tabular-nums", moneyClass(t.xd))}>{rub(t.xd)}</b>
          </span>
          <span>
            Ошибки и обработка поставок:{" "}
            <b className={cn("tabular-nums", moneyClass(t.sup))}>{rub(t.sup)}</b>
          </span>
          <span>
            Всего: <b className={cn("tabular-nums", moneyClass(t.total))}>{rub(t.total)}</b>
          </span>
        </div>
      )}
      {q.data && (
        <div className="overflow-x-auto rounded-md border bg-card">
          <table className="w-full min-w-[1000px] text-xs">
            <thead className="bg-muted/40 text-[11px]">
              <tr>
                <th className={cn(thClass, "text-left")}>Заявка</th>
                <th className={cn(thClass, "text-left")}>Слот</th>
                <th className={cn(thClass, "text-left")}>Кластер</th>
                <th className={thClass}>План / факт, шт.</th>
                <th className={thClass}>Кросс-док</th>
                <th className={thClass}>Ошибки</th>
                <th className={thClass}>Всего</th>
                <th className={cn(thClass, "text-left")}>Проблемы</th>
                <th className={thClass} />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={COLS} className="px-3 py-4 text-sm text-muted-foreground">
                    {onlyIssues ? "Поставок с проблемами в периоде нет." : "Поставок в периоде нет."}
                  </td>
                </tr>
              )}
              {rows.map((r) => {
                const id = `${r.legal_name}|${r.order_number ?? ""}`;
                return (
                  <SupplyLine
                    key={id}
                    row={r}
                    open={open === id}
                    onToggle={() => setOpen(open === id ? null : id)}
                    alloc={filters.alloc}
                    by={by}
                    onCard={onCard}
                  />
                );
              })}
              {q.data.unlinked && hasUnlinked(q.data.unlinked) && (
                <tr className="border-t bg-muted/30 font-medium" data-testid="supplies-unlinked">
                  <td className="px-2 py-1" colSpan={4}>
                    Не привязано к заявке
                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                      начисления кросс-докинга и ошибок поставок периода без номера заявки
                    </span>
                  </td>
                  <td className={cn(tdNum, moneyClass(q.data.unlinked.xd))}>
                    {rub(q.data.unlinked.xd)}
                  </td>
                  <td className={cn(tdNum, moneyClass(q.data.unlinked.sup))}>
                    {rub(q.data.unlinked.sup)}
                  </td>
                  <td className={cn(tdNum, moneyClass(q.data.unlinked.total))}>
                    {rub(q.data.unlinked.total)}
                  </td>
                  <td colSpan={2} />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function hasUnlinked(u: { xd: number; sup: number; total: number }): boolean {
  return Math.abs(u.xd) >= 0.005 || Math.abs(u.sup) >= 0.005 || Math.abs(u.total) >= 0.005;
}

function SupplyLine({
  row,
  open,
  onToggle,
  alloc,
  by,
  onCard,
}: {
  row: SupplyRow;
  open: boolean;
  onToggle: () => void;
  alloc: boolean;
  by: SupplyBy;
  onCard: (card: CardRef) => void;
}): React.ReactElement {
  const canOpen = row.order_number !== null;
  const number = row.order_number;
  return (
    <>
      <tr
        className={cn("border-t hover:bg-accent/30", canOpen && "cursor-pointer")}
        onClick={canOpen ? onToggle : undefined}
        aria-expanded={canOpen ? open : undefined}
      >
        <td className="px-2 py-1">
          <div className="flex items-center gap-1">
            <span className="w-4 text-muted-foreground">
              {canOpen && (open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />)}
            </span>
            <div>
              <div className="flex items-start gap-1">
                <span className="font-medium tabular-nums">{number ?? "без номера заявки"}</span>
                {number !== null && (
                  <CardButton
                    label={`Карточка поставки ${number}`}
                    title="Карточка поставки"
                    onOpen={() => onCard({ kind: "supply", key: number })}
                  />
                )}
              </div>
              <div className="text-xs text-muted-foreground">{row.legal_name}</div>
            </div>
          </div>
        </td>
        <td className="px-2 py-1 tabular-nums">{fmtDate(row.slot_date)}</td>
        <td className="px-2 py-1">{row.cluster ?? "—"}</td>
        <td className={tdNum}>
          {row.plan_units === null ? "—" : int(row.plan_units)} /{" "}
          {row.fact_units === null ? "—" : int(row.fact_units)}
        </td>
        <td className={cn(tdNum, moneyClass(row.xd))}>
          {rub(row.xd)}
          <XdPerUnitLine row={row} by={by} />
        </td>
        <td className={cn(tdNum, moneyClass(row.sup))}>{rub(row.sup)}</td>
        <td className={cn(tdNum, "font-medium", moneyClass(row.total))}>{rub(row.total)}</td>
        <td className="px-2 py-1">
          {row.has_diag ? (
            <IssueChips issues={row.issues} />
          ) : (
            <span className="text-xs text-muted-foreground">нет диагноза</span>
          )}
        </td>
        <td className="px-2 py-1 text-right text-xs text-muted-foreground">
          {row.has_sku_acts ? "акт по SKU" : ""}
        </td>
      </tr>
      {open && row.order_number !== null && (
        <tr className="bg-muted/20">
          <td colSpan={COLS} className="px-4 py-3">
            <SupplyDetail row={row} orderNumber={row.order_number} alloc={alloc} />
          </td>
        </tr>
      )}
    </>
  );
}

function SupplyDetail({
  row,
  orderNumber,
  alloc,
}: {
  row: SupplyRow;
  orderNumber: string;
  alloc: boolean;
}): React.ReactElement {
  const q = useSupplyDetail(orderNumber, row.legal_name, alloc);
  return (
    <div className="space-y-4">
      {/* диагноз — уже в строке списка, без ожидания детали */}
      <SupplyDiagSection supplies={row.supplies} />
      {q.isLoading && <Loading text="Загружаю акт и начисления…" />}
      {q.isError && <ErrorBox error={q.error} what="разбор заявки" />}
      {q.data && (
        <>
          <SupplyActSection items={q.data.act_items} />
          <SupplyAccrualsSection detail={q.data} />
        </>
      )}
    </div>
  );
}

/** Вторая строка ячейки «Кросс-док»: сколько он стоил на одну принятую единицу заявки. */
function XdPerUnitLine({ row, by }: { row: SupplyRow; by: SupplyBy }): React.ReactElement | null {
  const x = xdPerUnit(
    row.xd,
    row.fact_units,
    row.supplies.map((s) => s.fact_units),
  );
  if (x === null) return null;
  const { text, title } = xdPerUnitLabel(x, by);
  return (
    <div className="text-[11px] text-muted-foreground" title={title}>
      {text}
    </div>
  );
}
